import { Getter, State } from 'vuex-simple';
import { BaseModule } from '@/HostApp/store/ApsModules/BaseModule';
import bizAPIModule from '@/HostApp/store/BizApiStorage/bizApiStorage';
import alertServiceModule from '@/HostApp/services/AlertService/AlertServiceModule';
import analyticsModule from '@/HostApp/services/Analytics/AnalyticsModule';

export class ApscModule extends BaseModule {
	constructor() {
		super();

		//Loading
		this.createProxyHandler(this, 'showLoading', this.showLoading);
		this.createProxyHandler(this, 'hideLoading', this.hideLoading);
		this.createProxyHandler(this, 'setLoadingCount', this.setLoadingCount);

		//Notifications
		this.createProxyHandler(this, 'showNotification', this.showNotification);

		//Messages
		this.createProxyHandler(this, '_displayMessage', this.displayMessage);
		this.createProxyHandler(this, '_removeAllMessages', this.removeAllMessages);
		this.createProxyHandler(this, 'displayMessage', this.displayMessage);
		this.createProxyHandler(this, 'injectMessage', this.injectMessage);
		this.createProxyHandler(this, 'removeAllMessages', this.removeAllMessages);

		//Popups
		this.createProxyHandler(this, '_gotoPopup', this.gotoPopup);
		this.createProxyHandler(this, '_showConfirm', this.showConfirm);

		//Buttons
		this.createProxyHandler(this, 'buttonState', this.buttonState);

		//_bizStorageAction
		this.createProxyHandler(this, '_bizStorageAction', this._bizStorageAction);
	}

	/**
	 * @State decorator creates a handler 'propName' : {
	 * 		get: () => { return Vuex.store.propName },
	 * 		set: (value) => { Vuex.store.commit(propName, value)}
	 * 	}
	 *
	 * 	to make any value from window.aps reactive, just describe it here with decorator @State()
	 **/

	@State()
	public showCounter = 0;

	public dialogType: 'confirm' | 'popup' = 'popup';

	public buttonState = {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		apply: (target: Common.FnAny, thisArg: any, args: ArrayLike<any>): any => {
			return Reflect.apply(target, thisArg, args);
		}
	};

	public handleDialogClosed(status: 'submit' | 'cancel'): void {
		if (this.dialogType === 'confirm') {
			//@ts-ignore
			window.aps.app._onHideConfirm(status === 'submit');
		}
		this.dialogType = 'popup';
	}

	public _bizStorageAction = {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		apply: (target: Common.FnAny, thisArg: any, args: ArrayLike<any>): any => {
			const [methods] = Array.from(args);
			const { method, data } = methods;
			const [key, value] = data || [];

			if (key && method) {
				switch (method) {
					case 'put': {
						return bizAPIModule.set(key, value);
					}
					case 'get': {
						return bizAPIModule.get(key);
					}
					case 'remove': {
						return bizAPIModule.remove(key);
					}
					case 'clear': {
						break;
					}
				}
			}
		}
	};

	public showConfirm = {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		apply: (target: Common.FnAny, thisArg: any, args: ArrayLike<any>): any => {
			this.dialogType = 'confirm';
			return Reflect.apply(target, thisArg, args);
		}
	};

	public gotoPopup = {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		apply: (target: Common.FnAny, thisArg: any, args: ArrayLike<any>): any => {
			this.dialogType = 'popup';
			return Reflect.apply(target, thisArg, args);
		}
	};

	public injectMessage = {
		apply: (
			target: Common.FnAny,
			thisArg: never,
			args: [APS.IAlertMessageContext | string, string, string, string?]
		): void => {
			if (this.isVueWorkspace) {
				const message = Array.from(args) as [APS.IAlertMessageContext, string, string, string];
				if (typeof message[0] === 'string') {
					message.unshift({} as APS.IAlertMessageContext);
				}
				alertServiceModule.addMessage(message);
			} else {
				Reflect.apply(target, thisArg, args);
			}
		}
	};

	public displayMessage = {
		apply: (
			target: Common.FnAny,
			thisArg: never,
			args: [APS.IAlertMessageContext | string, string, string]
		): void => {
			if (this.isVueWorkspace) {
				const message = Array.from(args) as [APS.IAlertMessageContext, string, string];
				if (typeof message[0] === 'string') {
					message.unshift({} as APS.IAlertMessageContext);
				}
				alertServiceModule.addMessage(message);
			} else {
				Reflect.apply(target, thisArg, args);
			}
		}
	};

	public removeAllMessages = {
		apply: (target: Common.FnAny, thisArg: never, args: never): void => {
			if (this.isVueWorkspace) {
				alertServiceModule.removeAllMessages();
			} else {
				Reflect.apply(target, thisArg, args);
			}
		}
	};

	public showNotification = {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		apply: (target: Common.FnAny, thisArg: any, args: ArrayLike<any>): any => {
			if (this.isVueWorkspace) {
				window.dispatchEvent(new CustomEvent('HOST-SHOW-NOTIFICATION', { detail: Array.from(args) }));
			} else {
				Reflect.apply(target, thisArg, args);
			}
		}
	};

	public showLoading = {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		apply: (target: Common.FnAny, thisArg: any, args: ArrayLike<any>): any => {
			this.showCounter++;
			return Reflect.apply(target, thisArg, args);
		}
	};

	public hideLoading = {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		apply: (target: Common.FnAny, thisArg: any, args: ArrayLike<any>): any => {
			if (this.showCounter > 0) {
				this.showCounter--;
			}
			return Reflect.apply(target, thisArg, args);
		}
	};

	public setLoadingCount = {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		apply: (target: Common.FnAny, thisArg: any, args: ArrayLike<any>): any => {
			if (this.isVueWorkspace) {
				if (typeof args[0] === 'number') {
					this.showCounter = args[0];
				}
			}

			return Reflect.apply(target, thisArg, args);
		}
	};

	public gotoURL(url: string): void {
		//
	}

	public ready(): void {
		//
	}

	public loadPackage(arg: string): Promise<APS.IApsContext> {
		return Promise.resolve({} as APS.IApsContext);
	}

	public gotoView(
		viewId: string,
		resourceId: string | null,
		parameters: APS.INavigationParameters,
		subscriptionId?: string
	): void {
		//
	}

	@Getter()
	protected get isVueWorkspace(): boolean {
		return window.aps.navigation.isVueWorkspace;
	}

	@Getter()
	public get showGlobalLoading(): boolean {
		return this.showCounter > 0;
	}

	public pushData(eventData: Analytics.ITypedData): void {
		analyticsModule.pushData(eventData);
	}

	public pushException(viewId: string, viewLabel: string, message: string, type?: string): void {
		analyticsModule.pushException(viewId, viewLabel, message, type);
	}

	public pushAnalytics(eventData: Analytics.IGAEvent): void {
		analyticsModule.pushData({ data: eventData });
	}

	public next(returnValue: APS.IReturnValue): void {
		//
	}

	public prev(returnValue: APS.IReturnValue): void {
		//
	}

	public _goBack(): void {
		//
	}
}

const apscModule = new ApscModule();

export default apscModule;
